import React, { useState } from 'react';
import useClass from '../hooks/useClass';
import { GRADE_OPTIONS, SCHOOL_YEARS, getCurrentSchoolYear } from '../../class/constants';
import { useSchoolTeacherOptions } from '../../class/hooks/useSchoolTeacherOptions';

interface CreateClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId: string;
  schoolName: string;
}

const CreateClassModal: React.FC<CreateClassModalProps> = ({ isOpen, onClose, schoolId, schoolName }) => {
  const { createClass, isCreating } = useClass({schoolId});
  const { data: teachers = [], isLoading: isLoadingTeachers } = useSchoolTeacherOptions(schoolId);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    grade: '',
    teacherId: '',
    schoolYear: getCurrentSchoolYear(),
    status: 'active' as const
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tên lớp học là bắt buộc';
    }

    if (!formData.grade.trim()) {
      newErrors.grade = 'Khối là bắt buộc';
    }

    if (!formData.schoolYear.trim()) {
      newErrors.schoolYear = 'Năm học là bắt buộc';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    createClass({
      ...formData,
      schoolId,
      schoolName
    });
    onClose();
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      code: '',
      grade: '',
      teacherId: '',
      schoolYear: getCurrentSchoolYear(),
      status: 'active'
    });
    setErrors({});
  };

  const handleClose = () => {
    if (!isCreating) {
      handleReset();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg mr-3">
                <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Tạo lớp học mới</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">Trường: {schoolName}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tên lớp học */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Tên lớp học <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="10A1"
              />
              {errors.name && (
                <p className="text-xs text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Mã lớp học */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Mã lớp học
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.code ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="10A1"
              />
              {errors.code && (
                <p className="text-xs text-red-500">{errors.code}</p>
              )}
            </div>

            {/* Khối */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Khối <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.grade}
                onChange={(e) => handleChange('grade', e.target.value)}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.grade ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Chọn khối</option>
                {GRADE_OPTIONS.map((grade) => (
                  <option key={grade.value} value={grade.value}>{grade.label}</option>
                ))}
              </select>
              {errors.grade && (
                <p className="text-xs text-red-500">{errors.grade}</p>
              )}
            </div>

            {/* Năm học */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Năm học <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.schoolYear}
                onChange={(e) => handleChange('schoolYear', e.target.value)}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.schoolYear ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Chọn năm học</option>
                {SCHOOL_YEARS.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {errors.schoolYear && (
                <p className="text-xs text-red-500">{errors.schoolYear}</p>
              )}
            </div>

            {/* Giáo viên chủ nhiệm */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Giáo viên chủ nhiệm
              </label>
              <select
                value={formData.teacherId}
                onChange={(e) => handleChange('teacherId', e.target.value)}
                disabled={isLoadingTeachers}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50"
              >
                <option value="">
                  {isLoadingTeachers
                    ? 'Đang tải...'
                    : teachers.length === 0
                      ? 'Trường chưa có giáo viên'
                      : 'Chọn giáo viên'}
                </option>
                {teachers.map((teacher) => (
                  <option key={teacher.value} value={teacher.value}>{teacher.label}</option>
                ))}
              </select>
            </div>

            {/* Trường học - disabled */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Trường học
              </label>
              <input
                type="text"
                value={schoolName}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Đang tạo...' : 'Tạo lớp học'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateClassModal;
